import { toast } from 'sonner';

export function useSharePost() {
  const getPostUrl = (postId: string): string => {
    return `${window.location.origin}/wall/post/${postId}`;
  };

  const copyPostLink = async (postId: string): Promise<boolean> => {
    const url = getPostUrl(postId);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
      return true;
    } catch (error) {
      toast.error('Failed to copy link');
      return false;
    }
  };

  const shareToTwitter = (postId: string, text?: string) => {
    const url = getPostUrl(postId);
    const tweetText = text || 'Check out this post on RecruitLook!';
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = (postId: string) => {
    const url = getPostUrl(postId);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  const shareToLinkedIn = (postId: string) => {
    const url = getPostUrl(postId);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
  };

  const shareViaSms = (postId: string) => {
    const url = getPostUrl(postId);
    const smsUrl = `sms:?body=${encodeURIComponent(`Check this out: ${url}`)}`;
    window.location.href = smsUrl;
  };

  const shareNative = async (postId: string, text?: string): Promise<boolean> => {
    const url = getPostUrl(postId);
    const shareData = {
      title: 'RecruitLook Post',
      text: text || 'Check out this post on RecruitLook!',
      url,
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
        return true;
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          // User cancelled - not an error
          return await copyPostLink(postId);
        }
        return false;
      }
    }
    
    // Fallback to copy
    return await copyPostLink(postId);
  };

  const canNativeShare = (): boolean => {
    return typeof navigator.share === 'function';
  };

  return {
    getPostUrl,
    copyPostLink,
    shareToTwitter,
    shareToFacebook,
    shareToLinkedIn,
    shareViaSms,
    shareNative,
    canNativeShare,
  };
}
